/**
 * UUID v7 (RFC 9562 §5.7) — time-ordered identifiers generated in application
 * code, never by the database.
 *
 * Why v7 and why in the application: primary keys are generated before the
 * INSERT so an aggregate and its outbox event can be written in one transaction
 * with the id already known. The 48-bit millisecond prefix makes the values
 * roughly monotonic, which keeps B-tree inserts on the right-hand edge of the
 * index instead of scattering them the way uuid v4 does.
 *
 * Layout, most significant bit first:
 *
 *   ┌────────────────────────┬─────────┬──────────┬─────────┬────────────────┐
 *   │ unix_ts_ms (48)        │ ver (4) │ rand_a   │ var (2) │ rand_b (62)    │
 *   │                        │ = 0111  │ (12)     │ = 10    │                │
 *   └────────────────────────┴─────────┴──────────┴─────────┴────────────────┘
 *
 * `rand_a` carries a monotonic counter rather than pure randomness — RFC 9562
 * §6.2 "fixed-bit-length dedicated counter". Without it, two ids minted in the
 * same millisecond have no defined order, and "time-ordered" silently stops
 * being true exactly under the load where ordering matters.
 *
 * Purity: the generator takes its clock and its randomness as arguments. The
 * exported `uuidV7` binds the ambient ones; every property of the algorithm is
 * tested through injected, deterministic sources.
 */
/** 2^48 - 1: the largest instant representable in the 48-bit timestamp. */
const MAX_TIMESTAMP_MS = 0xffff_ffff_ffff;
/** `rand_a` is 12 bits, so the counter wraps at 4096. */
const COUNTER_LIMIT = 0x1000;
/**
 * Counters seed into the low 10 bits, leaving at least 3072 increments of
 * headroom before a wrap. Seeding randomly rather than at zero keeps the number
 * of ids issued in a millisecond from being readable off the identifier.
 */
const COUNTER_SEED_MASK = 0x03ff;
const UUID_V7_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
function defaultRandomBytes(length) {
    const bytes = new Uint8Array(length);
    globalThis.crypto.getRandomValues(bytes);
    return bytes;
}
/**
 * Calls `randomBytes` and enforces its contract.
 *
 * A source that silently returns fewer bytes than asked would zero-fill the
 * entropy, so this fails loudly instead.
 */
function takeBytes(randomBytes, length) {
    const bytes = randomBytes(length);
    if (bytes.length < length) {
        throw new TypeError(`randomBytes must return at least ${length} bytes, received ${bytes.length}`);
    }
    return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}
function seedCounter(randomBytes) {
    return takeBytes(randomBytes, 2).getUint16(0) & COUNTER_SEED_MASK;
}
function encode(timestampMs, counter, tail) {
    const bytes = new Uint8Array(16);
    const out = new DataView(bytes.buffer);
    // 48-bit big-endian timestamp, split because DataView has no 48-bit write.
    out.setUint16(0, Math.floor(timestampMs / 2 ** 32));
    out.setUint32(2, timestampMs >>> 0);
    // Version 7 in the high nibble, then the 12-bit counter.
    out.setUint16(6, 0x7000 | (counter & 0x0fff));
    // Variant 0b10 in the top two bits, then 62 bits of randomness.
    out.setUint8(8, 0x80 | (tail.getUint8(0) & 0x3f));
    for (let i = 1; i < 8; i += 1) {
        out.setUint8(8 + i, tail.getUint8(i));
    }
    let hex = '';
    for (let i = 0; i < 16; i += 1) {
        hex += out.getUint8(i).toString(16).padStart(2, '0');
        if (i === 3 || i === 5 || i === 7 || i === 9)
            hex += '-';
    }
    return hex;
}
/**
 * Builds a generator with its own counter state.
 *
 * Successive calls return strictly increasing values, including within a single
 * millisecond and across a backwards clock step — the generator never emits an
 * id below one it has already emitted.
 */
export function createUuidV7Generator(options = {}) {
    const now = options.now ?? Date.now;
    const randomBytes = options.randomBytes ?? defaultRandomBytes;
    let lastMs = -1;
    let counter = 0;
    return function nextUuidV7() {
        const observed = now();
        if (!Number.isInteger(observed) || observed < 0 || observed > MAX_TIMESTAMP_MS) {
            throw new RangeError(`now() must return an integer between 0 and ${MAX_TIMESTAMP_MS}, received ${observed}`);
        }
        // Computed into locals first: `seedCounter` and `takeBytes` can both throw
        // on a misbehaving randomness source, and a generator left with a advanced
        // timestamp but a stale counter would be a silently degraded one. Nothing
        // is committed until every fallible step has succeeded.
        let nextMs = lastMs;
        let nextCounter;
        if (observed > lastMs) {
            nextMs = observed;
            nextCounter = seedCounter(randomBytes);
        }
        else {
            // Same millisecond, or the clock moved backwards (NTP correction, leap
            // smear, VM migration). Either way, hold the timestamp and advance the
            // counter so ordering survives.
            nextCounter = counter + 1;
            if (nextCounter >= COUNTER_LIMIT) {
                // Borrow a millisecond from the future rather than reuse a counter
                // value. Sorting stays correct; the timestamp runs at most a few ms
                // ahead under sustained bursts above 4096 ids/ms.
                nextMs = lastMs + 1;
                nextCounter = seedCounter(randomBytes);
            }
        }
        const tail = takeBytes(randomBytes, 8);
        lastMs = nextMs;
        counter = nextCounter;
        return encode(lastMs, counter, tail);
    };
}
/**
 * Process-wide generator bound to the ambient clock and Web Crypto.
 *
 * This is the one every caller should use, so all ids from a process share a
 * single monotonic counter.
 */
export const uuidV7 = createUuidV7Generator();
/** True when `value` is a well-formed UUID v7 string. */
export function isUuidV7(value) {
    return UUID_V7_PATTERN.test(value);
}
/**
 * Extracts the embedded creation time, in milliseconds since the Unix epoch.
 *
 * Useful for asserting ordering in tests and for coarse "when was this created"
 * questions without a round trip. It is not a substitute for `created_at`: the
 * counter can push the timestamp forward under burst load.
 */
export function uuidV7Timestamp(id) {
    if (!isUuidV7(id)) {
        throw new TypeError(`Not a UUID v7: ${id}`);
    }
    return Number.parseInt(id.slice(0, 8) + id.slice(9, 13), 16);
}
//# sourceMappingURL=uuid-v7.js.map